import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Bell, 
  DollarSign, 
  Save,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  TrendingUp,
  Percent,
  Calculator,
  Package,
  Settings2
} from "lucide-react";

const API = "/api";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

export default function PriceRulesPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Stok Uyarı Ayarları
  const [stockAlertSettings, setStockAlertSettings] = useState({
    enabled: true,
    lowStockThreshold: 5,
    criticalStockThreshold: 2,
    emailNotifications: false,
    notificationEmail: ""
  });
  
  // Fiyat Kuralları
  const [priceRules, setPriceRules] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: "",
    type: "margin", // margin, markup, fixed, discount
    value: 0,
    sourceCurrency: "USD",
    targetCurrency: "TRY",
    applyTo: "all", // all, category, product
    categoryFilter: "",
    productFilter: "",
    isActive: true
  });
  
  // Kur Bilgileri
  const [exchangeRates, setExchangeRates] = useState({
    USD_TRY: 32.50,
    EUR_TRY: 35.20,
    GBP_TRY: 41.10
  });

  useEffect(() => {
    fetchSettings();
    fetchPriceRules();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/stock-alerts?token=${token}`);
      if (response.data) {
        setStockAlertSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Settings fetch error:", error);
    }
  };

  const fetchPriceRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/price-rules?token=${token}`);
      setPriceRules(response.data.rules || []);
    } catch (error) {
      console.error("Price rules fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveStockAlertSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/stock-alerts?token=${token}`, stockAlertSettings);
      toast.success("Stok uyarı ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const openAddRuleDialog = () => {
    setEditingRule(null);
    setRuleForm({
      name: "",
      type: "margin",
      value: 0,
      sourceCurrency: "USD",
      targetCurrency: "TRY",
      applyTo: "all",
      categoryFilter: "",
      productFilter: "",
      isActive: true
    });
    setRuleDialogOpen(true);
  };

  const openEditRuleDialog = (rule) => {
    setEditingRule(rule);
    setRuleForm(rule);
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    if (!ruleForm.name) {
      toast.error("Kural adı zorunludur");
      return;
    }
    
    setSaving(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/price-rules/${editingRule.id}?token=${token}`, ruleForm);
        toast.success("Kural güncellendi");
      } else {
        await axios.post(`${API}/price-rules?token=${token}`, ruleForm);
        toast.success("Kural eklendi");
      }
      setRuleDialogOpen(false);
      fetchPriceRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!confirm("Bu kuralı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/price-rules/${ruleId}?token=${token}`);
      toast.success("Kural silindi");
      fetchPriceRules();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await axios.put(`${API}/price-rules/${rule.id}?token=${token}`, {
        ...rule,
        isActive: !rule.isActive
      });
      fetchPriceRules();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const getRuleTypeLabel = (type) => {
    switch (type) {
      case "margin": return "Kar Marjı (%)";
      case "markup": return "Markup (%)";
      case "fixed": return "Sabit Ekleme";
      case "discount": return "İndirim (%)";
      default: return type;
    }
  };

  const calculateExample = () => {
    const basePrice = 100; // USD
    const rate = exchangeRates.USD_TRY || 32.50;
    let finalPrice = basePrice * rate;
    
    if (ruleForm.type === "margin") {
      finalPrice = finalPrice / (1 - ruleForm.value / 100);
    } else if (ruleForm.type === "markup") {
      finalPrice = finalPrice * (1 + ruleForm.value / 100);
    } else if (ruleForm.type === "fixed") {
      finalPrice = finalPrice + ruleForm.value;
    } else if (ruleForm.type === "discount") {
      finalPrice = finalPrice * (1 - ruleForm.value / 100);
    }
    
    return finalPrice.toFixed(2);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="price-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Fiyat & Stok Yönetimi</h1>
          <p className="text-slate-500 mt-1">Otomatik fiyat kuralları ve stok uyarı ayarları</p>
        </div>
      </div>

      <Tabs defaultValue="price-rules" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="price-rules" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Fiyat Kuralları
          </TabsTrigger>
          <TabsTrigger value="stock-alerts" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Stok Uyarıları
          </TabsTrigger>
          <TabsTrigger value="exchange-rates" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Döviz Kurları
          </TabsTrigger>
        </TabsList>

        {/* Fiyat Kuralları Tab */}
        <TabsContent value="price-rules" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-blue-500" />
                    Fiyat Kuralları
                  </CardTitle>
                  <CardDescription>
                    Shopify'a aktarılacak ürünler için otomatik fiyat hesaplama kuralları
                  </CardDescription>
                </div>
                <Button onClick={openAddRuleDialog} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Yeni Kural
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : priceRules.length === 0 ? (
                <div className="text-center py-12">
                  <Calculator className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henüz fiyat kuralı tanımlanmadı</p>
                  <Button onClick={openAddRuleDialog} className="mt-4" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    İlk Kuralı Ekle
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kural Adı</TableHead>
                      <TableHead>Tip</TableHead>
                      <TableHead>Değer</TableHead>
                      <TableHead>Kapsam</TableHead>
                      <TableHead className="text-center">Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {priceRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">{rule.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getRuleTypeLabel(rule.type)}</Badge>
                        </TableCell>
                        <TableCell>
                          {rule.type === "fixed" ? (
                            <span className="font-mono">{rule.value} {rule.targetCurrency}</span>
                          ) : (
                            <span className="font-mono">%{rule.value}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={rule.applyTo === "all" ? "bg-blue-500" : "bg-purple-500"}>
                            {rule.applyTo === "all" ? "Tüm Ürünler" : rule.applyTo === "category" ? "Kategori" : "Ürün"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={rule.isActive}
                            onCheckedChange={() => toggleRuleActive(rule)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => openEditRuleDialog(rule)}>
                              <Pencil className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => deleteRule(rule.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stok Uyarıları Tab */}
        <TabsContent value="stock-alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                Stok Uyarı Ayarları
              </CardTitle>
              <CardDescription>
                Düşük stok seviyelerinde otomatik uyarı alın
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Stok Uyarılarını Aktif Et</Label>
                  <p className="text-sm text-slate-500">Dashboard'da düşük stoklu ürünleri görüntüle</p>
                </div>
                <Switch
                  checked={stockAlertSettings.enabled}
                  onCheckedChange={(v) => setStockAlertSettings({...stockAlertSettings, enabled: v})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lowStock">Düşük Stok Eşiği</Label>
                  <Input
                    id="lowStock"
                    type="number"
                    value={stockAlertSettings.lowStockThreshold}
                    onChange={(e) => setStockAlertSettings({
                      ...stockAlertSettings, 
                      lowStockThreshold: parseInt(e.target.value) || 0
                    })}
                  />
                  <p className="text-xs text-slate-500">Bu değerin altındaki ürünler uyarı listesine eklenir</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="criticalStock">Kritik Stok Eşiği</Label>
                  <Input
                    id="criticalStock"
                    type="number"
                    value={stockAlertSettings.criticalStockThreshold}
                    onChange={(e) => setStockAlertSettings({
                      ...stockAlertSettings, 
                      criticalStockThreshold: parseInt(e.target.value) || 0
                    })}
                  />
                  <p className="text-xs text-slate-500">Bu değerin altındaki ürünler acil olarak işaretlenir</p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>E-posta Bildirimleri</Label>
                    <p className="text-sm text-slate-500">Kritik stok durumlarında e-posta al</p>
                  </div>
                  <Switch
                    checked={stockAlertSettings.emailNotifications}
                    onCheckedChange={(v) => setStockAlertSettings({...stockAlertSettings, emailNotifications: v})}
                  />
                </div>
                
                {stockAlertSettings.emailNotifications && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Bildirim E-postası</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@sirket.com"
                      value={stockAlertSettings.notificationEmail}
                      onChange={(e) => setStockAlertSettings({
                        ...stockAlertSettings, 
                        notificationEmail: e.target.value
                      })}
                    />
                  </div>
                )}
              </div>

              <Button onClick={saveStockAlertSettings} disabled={saving}>
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Ayarları Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Döviz Kurları Tab */}
        <TabsContent value="exchange-rates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Döviz Kurları
              </CardTitle>
              <CardDescription>
                Fiyat hesaplamalarında kullanılacak döviz kurları
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-blue-900">USD/TRY</span>
                    <DollarSign className="w-5 h-5 text-blue-500" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={exchangeRates.USD_TRY}
                    onChange={(e) => setExchangeRates({...exchangeRates, USD_TRY: parseFloat(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-purple-900">EUR/TRY</span>
                    <span className="text-purple-500 font-bold">€</span>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={exchangeRates.EUR_TRY}
                    onChange={(e) => setExchangeRates({...exchangeRates, EUR_TRY: parseFloat(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-emerald-900">GBP/TRY</span>
                    <span className="text-emerald-500 font-bold">£</span>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={exchangeRates.GBP_TRY}
                    onChange={(e) => setExchangeRates({...exchangeRates, GBP_TRY: parseFloat(e.target.value) || 0})}
                    className="bg-white"
                  />
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Otomatik Kur Güncelleme</p>
                    <p className="text-sm text-amber-600 mt-1">
                      Döviz kurları şu an manuel olarak girilmektedir. 
                      İleriki sürümlerde TCMB veya diğer kaynaklardan otomatik çekilebilir.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button onClick={() => toast.success("Kurlar kaydedildi")}>
                <Save className="w-4 h-4 mr-2" />
                Kurları Kaydet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Kural Ekleme/Düzenleme Dialog */}
      <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? "Kuralı Düzenle" : "Yeni Fiyat Kuralı"}</DialogTitle>
            <DialogDescription>
              Shopify'a aktarılan ürünlerin fiyatlarını otomatik hesaplayın
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ruleName">Kural Adı</Label>
              <Input
                id="ruleName"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})}
                placeholder="Örn: Yurt Dışı Kar Marjı"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kural Tipi</Label>
                <Select 
                  value={ruleForm.type} 
                  onValueChange={(v) => setRuleForm({...ruleForm, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="margin">Kar Marjı (%)</SelectItem>
                    <SelectItem value="markup">Markup (%)</SelectItem>
                    <SelectItem value="fixed">Sabit Ekleme</SelectItem>
                    <SelectItem value="discount">İndirim (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ruleValue">Değer</Label>
                <Input
                  id="ruleValue"
                  type="number"
                  step="0.01"
                  value={ruleForm.value}
                  onChange={(e) => setRuleForm({...ruleForm, value: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kaynak Para Birimi</Label>
                <Select 
                  value={ruleForm.sourceCurrency} 
                  onValueChange={(v) => setRuleForm({...ruleForm, sourceCurrency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Hedef Para Birimi</Label>
                <Select 
                  value={ruleForm.targetCurrency} 
                  onValueChange={(v) => setRuleForm({...ruleForm, targetCurrency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Uygulama Kapsamı</Label>
              <Select 
                value={ruleForm.applyTo} 
                onValueChange={(v) => setRuleForm({...ruleForm, applyTo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Ürünler</SelectItem>
                  <SelectItem value="category">Belirli Kategori</SelectItem>
                  <SelectItem value="product">Belirli Ürün</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Örnek Hesaplama */}
            <div className="bg-slate-50 rounded-lg p-4 border">
              <p className="text-sm font-medium text-slate-700 mb-2">Örnek Hesaplama</p>
              <div className="flex items-center gap-2 text-sm">
                <span>100 USD</span>
                <span className="text-slate-400">→</span>
                <span className="font-bold text-emerald-600">{calculateExample()} TRY</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                (Kur: 1 USD = {exchangeRates.USD_TRY} TRY)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRuleDialogOpen(false)}>İptal</Button>
            <Button onClick={saveRule} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingRule ? "Güncelle" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
